import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';

// Get environment variables with fallbacks for development
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://wmrinjplbeqxlcbabujh.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6IndtcmluanBsYmVxeGxjYmFidWpoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDkzMjMyNzQsImV4cCI6MjA2NDg5OTI3NH0.1FFmPQOM8slthlIwR2udWWGzn9YQ2so0SC0pFMZUyPA';

// Create a singleton instance of the Supabase client
let supabaseInstance: SupabaseClient<Database> | null = null;

export function getSupabaseClient(): SupabaseClient<Database> {
  if (!supabaseInstance) {
    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error('Missing Supabase environment variables');
    }

    supabaseInstance = createClient<Database>(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: 'rightnowaid-auth-token' // Ensure consistent storage key
      },
      global: {
        headers: {
          'x-elevenlabs-api-key': import.meta.env.VITE_ELEVENLABS_API_KEY || ''
        }
      }
    });
  }

  return supabaseInstance;
}

// Export the singleton instance
export const supabase = getSupabaseClient();

// Export default for backward compatibility
export default supabase;

// Mock data for program search
const mockPrograms = [
  {
    id: '1',
    name: 'Food Assistance Program',
    description: 'Provides food assistance to low-income families and individuals.',
    eligibility: ['Income below 200% of federal poverty level', 'Resident of service area'],
    services: ['Food pantry', 'SNAP application assistance', 'Nutrition education'],
    contact: {
      phone: '(555) 123-4567',
      email: 'food@example.org',
      website: 'https://example.org/food'
    },
    address: {
      street: '123 Main St',
      city: 'Anytown',
      state: 'CA',
      zip: '12345'
    },
    hours: {
      'Monday': '9:00 AM - 5:00 PM',
      'Tuesday': '9:00 AM - 5:00 PM',
      'Wednesday': '9:00 AM - 5:00 PM',
      'Thursday': '9:00 AM - 5:00 PM',
      'Friday': '9:00 AM - 5:00 PM'
    },
    languages: ['en', 'es'],
    distance: 2.5
  },
  {
    id: '2',
    name: 'Housing Support Services',
    description: 'Assists with housing-related needs including rental assistance and eviction prevention.',
    eligibility: ['Facing housing crisis', 'Income below 150% of federal poverty level'],
    services: ['Rental assistance', 'Eviction prevention', 'Housing counseling'],
    contact: {
      phone: '(555) 234-5678',
      email: 'housing@example.org',
      website: 'https://example.org/housing'
    },
    address: {
      street: '456 Oak Ave',
      city: 'Anytown',
      state: 'CA',
      zip: '12345'
    },
    hours: {
      'Monday': '10:00 AM - 6:00 PM',
      'Tuesday': '10:00 AM - 6:00 PM',
      'Wednesday': '10:00 AM - 6:00 PM',
      'Thursday': '10:00 AM - 6:00 PM',
      'Friday': '10:00 AM - 6:00 PM'
    },
    languages: ['en', 'es'],
    distance: 3.1
  }
];

// Mock implementation of Supabase Edge Functions
const mockFunctions = {
  'search-programs': async (params: any) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Filter programs based on zip code and radius
    const filteredPrograms = mockPrograms.filter(program => {
      // In a real implementation, this would use a geocoding service
      // to calculate actual distances between zip codes
      return program.distance <= (params.radius || 25);
    });

    return {
      data: { programs: filteredPrograms },
      error: null
    };
  },
  'get-program-details': async (params: any) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    const program = mockPrograms.find(p => p.id === params.programId);
    if (!program) {
      return {
        data: null,
        error: { message: 'Program not found' }
      };
    }

    return {
      data: { program },
      error: null
    };
  },
  'generate-appeal-letter': async (params: any) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    const program = mockPrograms.find(p => p.id === params.programId);
    if (!program) {
      return {
        data: null,
        error: { message: 'Program not found' }
      };
    }

    // Generate a mock appeal letter
    const letter = `
Dear ${program.name} Program Administrator,

I am writing to appeal the decision regarding my application for assistance. My name is ${params.userDetails.name}, and I am currently facing significant challenges that make it difficult to meet my basic needs.

${params.userDetails.situation}

I believe I meet the eligibility requirements for your program and would greatly benefit from the services you provide. I am committed to working with your organization to address my current situation and improve my circumstances.

Please consider my appeal and let me know if you need any additional information or documentation.

Thank you for your time and consideration.

Sincerely,
${params.userDetails.name}
${params.userDetails.address}
${params.userDetails.phone}
${params.userDetails.email}
    `.trim();

    return {
      data: { letter },
      error: null
    };
  },
  'generate-call-script': async (params: any) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    const program = mockPrograms.find(p => p.id === params.programId);
    if (!program) {
      return {
        data: null,
        error: { message: 'Program not found' }
      };
    }

    // Generate a mock call script
    const script = `
Hello, my name is ${params.userDetails.name}. I'm calling about the ${program.name}.

I'm interested in learning more about your services and how I might qualify for assistance.

${params.userDetails.situation}

Could you please tell me more about:
1. The application process
2. Required documentation
3. Expected timeline for assistance
4. Any other programs you might recommend

Thank you for your help.
    `.trim();

    return {
      data: { script },
      error: null
    };
  },
  'chat-ai': async (params: { message: string; language: string }) => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const responses = {
      EN: [
        "I understand you're looking for assistance. Let me help you find the right resources.",
        "Based on your situation, I recommend checking out our food assistance programs.",
        "Have you considered applying for housing support? I can guide you through the process.",
        "I can help you find local programs that match your needs. What specific assistance are you looking for?",
        "Let's explore what benefits you might be eligible for. Can you tell me more about your current situation?"
      ],
      ES: [
        "Entiendo que estás buscando ayuda. Déjame ayudarte a encontrar los recursos adecuados.",
        "Basado en tu situación, te recomiendo revisar nuestros programas de asistencia alimentaria.",
        "¿Has considerado solicitar apoyo para vivienda? Puedo guiarte en el proceso.",
        "Puedo ayudarte a encontrar programas locales que coincidan con tus necesidades. ¿Qué tipo de asistencia específica estás buscando?",
        "Exploremos qué beneficios podrías ser elegible. ¿Puedes contarme más sobre tu situación actual?"
      ]
    };

    const languageResponses = responses[params.language as keyof typeof responses] || responses.EN;
    const randomResponse = languageResponses[Math.floor(Math.random() * languageResponses.length)];
    
    return {
      data: { response: randomResponse },
      error: null
    };
  }
};

// Create a custom Supabase client with mock functions
const createMockSupabaseClient = () => {
  const client = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
    },
    global: {
      headers: {
        'x-elevenlabs-api-key': 'sk_d6b04f35d32fd712bafd9c65be1eaae6a7987e21cec0d95e'
      }
    }
  });

  const originalInvoke = client.functions.invoke;

  // Create a proxy for the functions object
  const functionsProxy = new Proxy(client.functions, {
    get(target, prop) {
      if (prop === 'invoke') {
        return async (name: string, options: any) => {
          const mockFunction = mockFunctions[name as keyof typeof mockFunctions];
          if (mockFunction) {
            return mockFunction(options.body);
          }
          return originalInvoke.call(target, name, options);
        };
      }
      return target[prop as keyof typeof target];
    }
  });

  // Create a proxy for the client
  return new Proxy(client, {
    get(target, prop) {
      if (prop === 'functions') {
        return functionsProxy;
      }
      return target[prop as keyof typeof target];
    }
  });
};

// Export the mock client
export const supabaseMock = createMockSupabaseClient();
